import { RackResponse } from "subModule/src/services/rack/interface";
import { getRackCode } from "subModule/src/services/rack/service";

/** Build shelf/bin code: if `{n}` is present replace it, otherwise append the index. */
export const applyCodePattern = (pattern: string, index: number) => {
  const base = String(pattern || "").trim();
  if (!base) return String(index);
  if (/\{n\}/i.test(base)) return base.replace(/\{n\}/gi, String(index));
  return `${base}${index}`;
};

export const buildBinAddressPreview = (
  rackCode: string,
  shelfPattern: string,
  binPattern: string,
  shelfCount: number,
  binsPerShelf: number,
) => {
  const shelves = Math.max(0, Math.floor(Number(shelfCount) || 0));
  const bins = Math.max(0, Math.floor(Number(binsPerShelf) || 0));
  const addresses: string[] = [];
  for (let s = 1; s <= shelves; s++) {
    const shelfCode = applyCodePattern(shelfPattern, s);
    for (let b = 1; b <= bins; b++) {
      const binCode = applyCodePattern(binPattern, b);
      addresses.push(`${rackCode}-${shelfCode}-${binCode}`);
    }
  }
  return addresses;
};

/** One row per shelf for preview UI */
export const buildShelfPreviewRows = (
  rackCode: string,
  shelfPattern: string,
  binPattern: string,
  shelfCount: number,
  binsPerShelf: number,
) => {
  const shelves = Math.max(0, Math.floor(Number(shelfCount) || 0));
  const bins = Math.max(0, Math.floor(Number(binsPerShelf) || 0));
  const rows: { shelfIndex: number; shelfCode: string; addresses: string[] }[] =
    [];
  for (let s = 1; s <= shelves; s++) {
    const shelfCode = applyCodePattern(shelfPattern, s);
    const addresses: string[] = [];
    for (let b = 1; b <= bins; b++) {
      const binCode = applyCodePattern(binPattern, b);
      addresses.push(`${rackCode}-${shelfCode}-${binCode}`);
    }
    rows.push({ shelfIndex: s, shelfCode, addresses });
  }
  return rows;
};

/** API: OPEN = 1, BIN = 2 */
export const INVENTORY_MODE_OPEN = 1;
export const INVENTORY_MODE_BIN = 2;

export const normalizeInventoryMode = (
  mode?: string | number | null,
): number | null => {
  if (mode == null || mode === "") return null;
  const n = Number(mode);
  if (n === INVENTORY_MODE_OPEN || n === INVENTORY_MODE_BIN) return n;
  const s = String(mode).toLowerCase();
  if (s === "open") return INVENTORY_MODE_OPEN;
  if (s === "bin") return INVENTORY_MODE_BIN;
  return null;
};

export const isBinInventoryMode = (mode?: string | number | null) =>
  normalizeInventoryMode(mode) === INVENTORY_MODE_BIN;

export const formatInventoryModeLabel = (mode?: string | number | null) => {
  const normalized = normalizeInventoryMode(mode);
  if (normalized == null) return "";
  return normalized === INVENTORY_MODE_BIN ? "BIN" : "OPEN";
};

export const countRackBins = (rack: RackResponse) =>
  (rack.shelves || []).reduce(
    (total, shelf) => total + (shelf.bins?.length || 0),
    0,
  );

export const fullBinAddress = (
  rack: RackResponse,
  shelfCode: string,
  binCode: string,
) => `${getRackCode(rack)}-${shelfCode}-${binCode}`;
